import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import AssistantChatLayout from './AssistantChatLayout';
import {
  extractAssistantEntityContext,
  getAssistantRouteLabel,
  resolveAssistantContextPath,
} from '../../utils/assistantContext';

const STORAGE_KEY = 'agua-andre-assistant-state-v2';
const LEGACY_STORAGE_KEY = 'agua-andre-assistant-history';
const MAX_STORED_CHATS = 8;
const PAYLOAD_HISTORY_LIMIT = 10;
const DEFAULT_CHAT_TITLE = 'Nuevo chat';
const INITIAL_SUGGESTIONS = [
  { label: 'Boletas adeudadas', route: '/administrativo/boletas-adeudadas' },
  { label: 'Tarifario', route: '/administrativo/tarifario' },
  { label: 'Control de pagos', route: '/administrativo/control-pagos' },
];

function AssistantWorkspace({ standalone = false, apiClient = axios, chatEndpoint = '/assistant/chat' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [assistantState, setAssistantState] = useState(() => loadStoredState());
  const [apiError, setApiError] = useState('');
  const bodyRef = useRef(null);

  const effectiveContextPath = useMemo(
    () => resolveAssistantContextPath(location.pathname),
    [location.pathname],
  );
  const entityContext = useMemo(
    () => extractAssistantEntityContext(effectiveContextPath),
    [effectiveContextPath],
  );
  const routeLabel = useMemo(
    () => getAssistantRouteLabel(effectiveContextPath),
    [effectiveContextPath],
  );
  const usesInheritedContext = effectiveContextPath !== location.pathname;
  const activeChat = useMemo(
    () =>
      assistantState.chats.find((chat) => chat.id === assistantState.activeChatId) ||
      assistantState.chats[0] ||
      null,
    [assistantState],
  );
  const activeMessages = activeChat?.messages || [];

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(assistantState));
      sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (error) {
      // sessionStorage puede no estar disponible.
    }
  }, [assistantState]);

  useEffect(() => {
    if (!bodyRef.current) {
      return;
    }

    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [activeChat?.id, activeMessages.length]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = input.trim();

    if (!trimmed || isSending || !activeChat) {
      return;
    }

    const userMessage = {
      id: createId('user'),
      role: 'user',
      content: trimmed,
      sources: [],
      suggestions: [],
      needsClarification: false,
      excludeFromContext: false,
    };
    const nextMessages = [...activeMessages, userMessage];

    setAssistantState((current) =>
      updateChat(current, activeChat.id, (chat) => ({
        ...chat,
        title:
          chat.title === DEFAULT_CHAT_TITLE ? buildChatTitle(trimmed) : chat.title,
        updatedAt: Date.now(),
        messages: [...chat.messages, userMessage],
      })),
    );
    setInput('');
    setIsSending(true);
    setApiError('');

    try {
      const response = await apiClient.post(chatEndpoint, {
        messages: buildPayloadMessages(nextMessages),
        currentRoute: effectiveContextPath,
        entityContext,
      });

      const assistantMessage = {
        id: createId('assistant'),
        role: 'assistant',
        content: response.data?.answer || 'No pude generar una respuesta útil.',
        sources: Array.isArray(response.data?.sources) ? response.data.sources : [],
        suggestions: Array.isArray(response.data?.suggestions)
          ? response.data.suggestions
          : [],
        needsClarification: Boolean(response.data?.needsClarification),
        excludeFromContext: false,
      };

      setAssistantState((current) =>
        updateChat(current, activeChat.id, (chat) => ({
          ...chat,
          updatedAt: Date.now(),
          messages: [...chat.messages, assistantMessage],
        })),
      );
    } catch (error) {
      const message =
        error.response?.data?.message ||
        'No pude comunicarme con el asistente en este momento.';

      setApiError(message);
      setAssistantState((current) =>
        updateChat(current, activeChat.id, (chat) => ({
          ...chat,
          updatedAt: Date.now(),
          messages: [
            ...chat.messages,
            {
              id: createId('assistant-error'),
              role: 'assistant',
              content:
                'No pude completar la consulta. Verificá la configuración de OpenAI o reintentá en unos segundos.',
              sources: [],
              suggestions: [],
              needsClarification: false,
              excludeFromContext: false,
            },
          ],
        })),
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleSuggestionClick = (route) => {
    if (!route) {
      return;
    }

    if (standalone) {
      window.open(route, '_blank', 'noopener,noreferrer');
    } else {
      navigate(route);
    }
  };

  const handleNewChat = () => {
    if (isSending) {
      return;
    }

    const nextChat = createChat();
    setAssistantState((current) =>
      normalizeState({
        activeChatId: nextChat.id,
        chats: [nextChat, ...current.chats],
      }),
    );
    setInput('');
    setApiError('');
  };

  const handleSelectChat = (chatId) => {
    if (isSending || chatId === assistantState.activeChatId) {
      return;
    }

    setAssistantState((current) =>
      normalizeState({
        ...current,
        activeChatId: chatId,
      }),
    );
    setInput('');
    setApiError('');
  };

  const handleDeleteChat = (chatId) => {
    if (isSending) {
      return;
    }

    setAssistantState((current) => {
      const remaining = current.chats.filter((chat) => chat.id !== chatId);

      if (!remaining.length) {
        const nextChat = createChat();
        return {
          activeChatId: nextChat.id,
          chats: [nextChat],
        };
      }

      return normalizeState({
        activeChatId:
          current.activeChatId === chatId ? remaining[0].id : current.activeChatId,
        chats: remaining,
      });
    });
    setInput('');
    setApiError('');
  };

  if (standalone) {
    return <AssistantChatLayout
      chats={assistantState.chats} activeChat={activeChat} messages={activeMessages}
      input={input} setInput={setInput} isSending={isSending} error={apiError}
      bodyRef={bodyRef} onSubmit={handleSubmit} onNew={handleNewChat}
      onSelect={handleSelectChat} onDelete={handleDeleteChat}
      onNavigate={handleSuggestionClick} shortcuts={INITIAL_SUGGESTIONS}
    />;
  }

  return (
    <section className={`grid ${standalone ? 'h-[calc(100dvh-1rem)] md:h-[calc(100dvh-2rem)]' : 'min-h-[calc(100vh-10rem)]'} grid-cols-1 overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white shadow-[0_24px_70px_rgba(8,39,55,0.08)] dark:border-slate-700 dark:bg-slate-900 lg:grid-cols-[17rem,minmax(0,1fr)]`}>
      <aside className={`${standalone ? 'hidden lg:block' : ''} border-b border-slate-200/80 bg-[linear-gradient(180deg,rgba(241,247,250,0.98)_0%,rgba(224,240,248,0.92)_100%)] dark:border-slate-700 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.98)_0%,rgba(8,25,36,0.98)_100%)] lg:border-b-0 lg:border-r`}>
        <div className="border-b border-slate-200/80 px-4 py-4 dark:border-slate-700/80">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700 dark:text-cyan-300">
            Sesiones
          </p>
          <h2 className="mt-2 text-xl font-black tracking-[-0.03em] text-slate-900 dark:text-white">
            Asistente administrativo
          </h2>
          <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
            Abrí un chat nuevo cuando quieras cortar el contexto anterior y seguir limpio.
          </p>
          <button
            type="button"
            onClick={handleNewChat}
            disabled={isSending}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,_#083043_0%,_#13a4ec_100%)] px-3 py-2.5 text-sm font-bold text-white shadow-[0_18px_40px_rgba(19,164,236,0.25)] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-[18px]">add_comment</span>
            Nuevo chat
          </button>
        </div>

        <div className={`${standalone ? 'max-h-24 lg:max-h-none lg:h-[calc(100dvh-17rem)]' : 'max-h-[18rem] lg:max-h-none lg:h-[calc(100vh-16rem)]'} space-y-2 overflow-y-auto px-3 py-3`}>
          {assistantState.chats.map((chat) => {
            const isActive = chat.id === activeChat?.id;

            return (
              <div key={chat.id} className="group relative">
                <button
                  type="button"
                  onClick={() => handleSelectChat(chat.id)}
                  disabled={isSending}
                  className={`w-full rounded-[1.3rem] border px-3 py-3 pr-10 text-left transition-all ${
                    isActive
                      ? 'border-cyan-300 bg-white text-slate-900 shadow-[0_16px_35px_rgba(8,39,55,0.12)] dark:border-cyan-400/50 dark:bg-slate-800 dark:text-white'
                      : 'border-transparent bg-white/65 text-slate-700 hover:border-cyan-100 hover:bg-white dark:bg-slate-800/45 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800/80'
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex h-2.5 w-2.5 rounded-full ${
                        isActive ? 'bg-cyan-500' : 'bg-slate-300 dark:bg-slate-600'
                      }`}
                    />
                    <p className="truncate text-sm font-bold tracking-[-0.02em]">
                      {chat.title}
                    </p>
                  </div>
                  <p className="mt-2 truncate text-[11px] uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                    {formatChatTimestamp(chat.updatedAt)}
                  </p>
                  <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {getChatPreview(chat)}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => handleDeleteChat(chat.id)}
                  disabled={isSending}
                  className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-transparent bg-white/80 text-slate-400 opacity-100 transition-all hover:border-rose-200 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-30 dark:bg-slate-900/90 dark:text-slate-500 dark:hover:border-rose-500/30 dark:hover:text-rose-300 lg:opacity-0 lg:group-hover:opacity-100"
                  title="Eliminar chat"
                >
                  <span className="material-symbols-outlined text-[16px]">close</span>
                </button>
              </div>
            );
          })}
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col">
        <div className="relative overflow-hidden border-b border-slate-200/80 bg-[radial-gradient(circle_at_top_left,_rgba(19,164,236,0.32),_transparent_52%),linear-gradient(135deg,_#082737_0%,_#0f4f68_48%,_#13a4ec_100%)] px-5 py-5 text-white">
          <div className="absolute -right-10 top-0 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -left-6 bottom-0 h-20 w-20 rounded-full bg-cyan-200/15 blur-2xl" />

          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-cyan-50">
                <span className="material-symbols-outlined text-sm">auto_awesome</span>
                Chat RAG interno
              </div>
              <h3 className={`${standalone ? 'hidden lg:block' : ''} text-2xl font-black tracking-[-0.03em]`}>
                {activeChat?.title || DEFAULT_CHAT_TITLE}
              </h3>
              {standalone && (
                <select
                  aria-label="Sesiones"
                  value={activeChat?.id || ''}
                  onChange={(event) => handleSelectChat(event.target.value)}
                  disabled={isSending}
                  className="max-w-full rounded-lg border-white/30 bg-slate-900 text-lg font-bold text-white lg:hidden"
                >
                  {assistantState.chats.map((chat) => (
                    <option key={chat.id} value={chat.id}>{chat.title}</option>
                  ))}
                </select>
              )}
              <p className="mt-2 text-sm text-cyan-50/90">
                Contexto activo: {routeLabel}
                {entityContext ? ' con cliente seleccionado' : ''}
                {usesInheritedContext ? ' · heredado de la ultima pantalla' : ''}
              </p>
            </div>

            <button
              type="button"
              onClick={handleNewChat}
              disabled={isSending}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-[18px]">refresh</span>
              Nuevo chat
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col bg-[linear-gradient(180deg,rgba(246,247,248,0.74)_0%,rgba(230,246,255,0.5)_100%)] dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.82)_0%,rgba(3,15,23,0.92)_100%)]">
          <div
            ref={bodyRef}
            className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 md:px-6 md:py-5"
          >
            {activeMessages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[92%] rounded-[1.4rem] px-4 py-3 shadow-sm md:max-w-[80%] ${
                    message.role === 'user'
                      ? 'rounded-br-md bg-slate-900 text-white dark:bg-cyan-500 dark:text-slate-950'
                      : 'rounded-bl-md border border-white/70 bg-white/92 text-slate-800 dark:border-slate-700 dark:bg-slate-800/92 dark:text-slate-100'
                  }`}
                >
                  <p className="whitespace-pre-wrap text-sm leading-6">{message.content}</p>

                  {message.sources?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {message.sources.map((source, index) => (
                        <span
                          key={`${source.title}-${index}`}
                          className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200"
                        >
                          {source.type === 'doc' ? 'Doc' : 'Dato'}: {source.title}
                          {source.section ? ` · ${source.section}` : ''}
                        </span>
                      ))}
                    </div>
                  )}

                  {message.suggestions?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {message.suggestions.map((suggestion, index) => (
                        <button
                          key={`${suggestion.route}-${index}`}
                          type="button"
                          onClick={() => handleSuggestionClick(suggestion.route)}
                          className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-900 transition-colors hover:bg-cyan-100 dark:border-cyan-500/40 dark:bg-cyan-500/10 dark:text-cyan-100 dark:hover:bg-cyan-500/20"
                        >
                          <span className="material-symbols-outlined text-sm">north_east</span>
                          {suggestion.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isSending && (
              <div className="flex justify-start">
                <div className="rounded-[1.4rem] rounded-bl-md border border-white/70 bg-white/92 px-4 py-3 text-sm text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-800/92 dark:text-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined animate-spin text-cyan-600 dark:text-cyan-300">
                      progress_activity
                    </span>
                    Analizando documentos y datos del sistema...
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200/80 bg-white/95 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/95 md:px-6">
            {apiError && (
              <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                {apiError}
              </div>
            )}

            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                <span className="material-symbols-outlined text-sm">history</span>
                Se envian solo los ultimos {PAYLOAD_HISTORY_LIMIT} mensajes
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-800 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-100">
                <span className="material-symbols-outlined text-sm">folder_supervised</span>
                {routeLabel}
              </span>
              {usesInheritedContext && (
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  <span className="material-symbols-outlined text-sm">history_toggle_off</span>
                  Contexto heredado
                </span>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="rounded-[1.4rem] border border-slate-200 bg-slate-50 p-2 shadow-inner dark:border-slate-700 dark:bg-slate-950/60">
                <textarea
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="Preguntame por deuda, planes, lecturas, estados o pedime que te lleve a una pantalla..."
                  className="min-h-[110px] w-full resize-none border-none bg-transparent px-2 py-2 text-sm leading-6 text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"
                  disabled={isSending}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                  {activeChat
                    ? `${countContextMessages(activeChat.messages)} mensajes en este chat`
                    : 'Chat listo'}
                </div>

                <button
                  type="submit"
                  disabled={isSending || !input.trim()}
                  className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(135deg,_#082737_0%,_#13a4ec_100%)] px-4 py-2.5 text-sm font-bold text-white shadow-[0_10px_28px_rgba(19,164,236,0.28)] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="material-symbols-outlined text-[18px]">
                    {isSending ? 'hourglass_top' : 'send'}
                  </span>
                  Consultar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function buildPayloadMessages(messages) {
  return messages
    .filter((message) => !message.excludeFromContext)
    .slice(-PAYLOAD_HISTORY_LIMIT)
    .map((message) => ({
      role: message.role,
      content: message.content,
    }));
}

function countContextMessages(messages) {
  return messages.filter((message) => !message.excludeFromContext).length;
}

function createChat(overrides = {}) {
  const id = overrides.id || createId('chat');
  const timestamp = overrides.createdAt || Date.now();
  const messages =
    Array.isArray(overrides.messages) && overrides.messages.length
      ? overrides.messages
      : [createInitialAssistantMessage(id)];

  return {
    id,
    title: overrides.title || DEFAULT_CHAT_TITLE,
    createdAt: timestamp,
    updatedAt: overrides.updatedAt || timestamp,
    messages,
  };
}

function createInitialAssistantMessage(chatId) {
  return {
    id: `assistant-welcome-${chatId}`,
    role: 'assistant',
    content:
      'Puedo ayudarte con deuda de clientes, planes de pago, lecturas, boletas adeudadas y navegacion del panel. Si venis desde una ficha de cliente, tambien heredo ese contexto.',
    sources: [],
    suggestions: INITIAL_SUGGESTIONS,
    needsClarification: false,
    excludeFromContext: true,
  };
}

function createId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function updateChat(state, chatId, updater) {
  return normalizeState({
    ...state,
    chats: state.chats.map((chat) => (chat.id === chatId ? updater(chat) : chat)),
  });
}

function buildChatTitle(content) {
  const compact = String(content || '')
    .replace(/[¿?]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!compact) {
    return DEFAULT_CHAT_TITLE;
  }

  return compact.length > 36 ? `${compact.slice(0, 36).trim()}...` : compact;
}

function getChatPreview(chat) {
  const visibleMessages = chat.messages.filter((message) => !message.excludeFromContext);
  const previewMessage = visibleMessages[visibleMessages.length - 1];

  if (!previewMessage) {
    return 'Sin mensajes todavia.';
  }

  if (previewMessage.content.length > 72) {
    return `${previewMessage.content.slice(0, 72).trim()}...`;
  }

  return previewMessage.content;
}

function formatChatTimestamp(timestamp) {
  if (!timestamp) {
    return 'Ahora';
  }

  const date = new Date(timestamp);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();

  if (sameDay) {
    return new Intl.DateTimeFormat('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
  }).format(date);
}

function normalizeState(rawState) {
  if (!rawState || typeof rawState !== 'object') {
    const initialChat = createChat();
    return {
      activeChatId: initialChat.id,
      chats: [initialChat],
    };
  }

  const chats = sortChats(
    (Array.isArray(rawState.chats) ? rawState.chats : [])
      .map((chat) => sanitizeChat(chat))
      .filter(Boolean),
  ).slice(0, MAX_STORED_CHATS);

  if (!chats.length) {
    const initialChat = createChat();
    return {
      activeChatId: initialChat.id,
      chats: [initialChat],
    };
  }

  const activeChatId = chats.some((chat) => chat.id === rawState.activeChatId)
    ? rawState.activeChatId
    : chats[0].id;

  return {
    activeChatId,
    chats,
  };
}

function sanitizeChat(chat) {
  if (!chat || typeof chat !== 'object') {
    return null;
  }

  const id = chat.id ? String(chat.id) : createId('chat');
  const messages = (Array.isArray(chat.messages) ? chat.messages : [])
    .map((message) => sanitizeMessage(message))
    .filter(Boolean);

  return {
    id,
    title: String(chat.title || DEFAULT_CHAT_TITLE),
    createdAt: Number(chat.createdAt) || Date.now(),
    updatedAt: Number(chat.updatedAt) || Number(chat.createdAt) || Date.now(),
    messages: messages.length ? messages : [createInitialAssistantMessage(id)],
  };
}

function sanitizeMessage(message) {
  if (!message || typeof message !== 'object') {
    return null;
  }

  if (message.role !== 'assistant' && message.role !== 'user') {
    return null;
  }

  const content = String(message.content || '').trim();
  if (!content) {
    return null;
  }

  return {
    id: message.id ? String(message.id) : createId(message.role),
    role: message.role,
    content,
    sources: Array.isArray(message.sources) ? message.sources : [],
    suggestions: Array.isArray(message.suggestions) ? message.suggestions : [],
    needsClarification: Boolean(message.needsClarification),
    excludeFromContext: Boolean(message.excludeFromContext),
  };
}

function sortChats(chats) {
  return [...chats].sort((left, right) => right.updatedAt - left.updatedAt);
}

function loadStoredState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      return normalizeState(JSON.parse(raw));
    }

    const legacyRaw = sessionStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const legacyMessages = JSON.parse(legacyRaw);
      if (Array.isArray(legacyMessages)) {
        const migratedChat = createChat({
          title: buildChatTitle(
            legacyMessages.find((message) => message?.role === 'user')?.content || '',
          ),
          messages: legacyMessages
            .map((message) => sanitizeMessage(message))
            .filter(Boolean),
        });

        return normalizeState({
          activeChatId: migratedChat.id,
          chats: [migratedChat],
        });
      }
    }
  } catch (error) {
    return normalizeState(null);
  }

  return normalizeState(null);
}

export default AssistantWorkspace;
